import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { TableColumnsType, Dropdown, MenuProps, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { Filter, Menu as MenuIcon } from 'iconsax-react';
import Button from '../atoms/Button';
import Search from '../categories/Search';
import CustomTable from '../atoms/CustomTable';
import Chip from '../atoms/Chip';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';
import DeleteModal from '../atoms/DeleteModal';
import {
  useDeletePostMutation,
  useGetBiddingPostsQuery,
} from '../../store/services/biddingPostsService';
import { BiddingPostItem } from '../../types/biddingPosts';
import { setPage, usePublicState } from '../../store/slices/publicSlice';
import BiddingPostsFilterModal, {
  BiddingPostsFilterValues,
} from './BiddingPostsFilterModal';
import { getBiddingPostStatusChipType } from './biddingPostStatusHelper';

interface BiddingPostsTableProps {
  isDisliked?: boolean;
}

export default function BiddingPostsTable({
  isDisliked: propIsDisliked,
}: BiddingPostsTableProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isDisliked = propIsDisliked ?? location.pathname.includes('disliked');
  const { search, page } = usePublicState();
  const searchValue = search?.['biddingPostsSearch'];
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<BiddingPostsFilterValues>({
    status: [],
    dateFrom: '',
    dateTo: '',
  });

  const handleConfirmDelete = async () => {
    if (!deletePostId) return;
    try {
      await deletePost({ postId: deletePostId }).unwrap();
      message.success(t('biddingPosts.deleteSuccess'));
      setDeletePostId(null);
    } catch {
      message.error(t('common.error'));
      setDeletePostId(null);
    }
  };

  const handleApplyFilters = (newFilters: BiddingPostsFilterValues) => {
    setFilters(newFilters);
    dispatch(setPage(0));
  };

  const handleResetFilters = () => {
    setFilters({
      status: [],
      dateFrom: '',
      dateTo: '',
    });
    dispatch(setPage(0));
  };

  // Real API with conditional disliked, search, status, and date range params
  const { data: biddingPostsData, isFetching } = useGetBiddingPostsQuery({
    page,
    size: 10,
    ...(searchValue ? { search: searchValue } : {}),
    ...(isDisliked ? { disliked: true } : {}),
    ...(filters.status.length ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  });

  const tableData: BiddingPostItem[] = biddingPostsData?.payload?.content ?? [];
  const totalElements = biddingPostsData?.payload?.totalElements ?? 0;

  dayjs.locale(language);

  const columns: TableColumnsType<BiddingPostItem> = useMemo(
    () => [
      {
        title: (
          <TitleText
            text={t('biddingPosts.postId')}
            className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid="bidding-posts-col-post-id"
          />
        ),
        dataIndex: 'serial',
        key: 'serial',
        render: (serial: string) => (
          <InfoText
            text={`#${serial}`}
            className="!font-normal !text-sm text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid={`bidding-posts-serial-${serial}`}
          />
        ),
      },
      {
        title: (
          <TitleText
            text={t('biddingPosts.postName')}
            className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid="bidding-posts-col-post-name"
          />
        ),
        dataIndex: 'postName',
        key: 'postName',
        render: (name: string, record) => (
          <InfoText
            text={name}
            className="!font-normal !text-sm text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid={`bidding-posts-name-${record.postId}`}
          />
        ),
      },
      {
        title: (
          <TitleText
            text={t('biddingPosts.postOwner')}
            className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid="bidding-posts-col-post-owner"
          />
        ),
        dataIndex: 'ownerName',
        key: 'ownerName',
        render: (owner: string, record) => (
          <InfoText
            text={owner}
            className="!font-normal !text-sm text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid={`bidding-posts-owner-${record.postId}`}
          />
        ),
      },
      {
        title: (
          <div
            className="flex justify-center"
            data-testid="bidding-posts-col-status-header"
          >
            <TitleText
              text={t('biddingPosts.postStatus')}
              className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none"
              data-testid="bidding-posts-col-post-status"
            />
          </div>
        ),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status: { name: string; code: string }, record) => (
          <div
            className="flex justify-center items-center"
            data-testid={`bidding-posts-status-container-${record.postId}`}
          >
            <Chip
              type={getBiddingPostStatusChipType(status)}
              className="!px-3.5 !py-1 !text-xs !font-medium !rounded-full"
              data-testid={`bidding-posts-status-chip-${record.postId}`}
            >
              {status?.name || '-'}
            </Chip>
          </div>
        ),
      },
      {
        title: (
          <TitleText
            text={t('biddingPosts.dateCreated')}
            className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid="bidding-posts-col-date-created"
          />
        ),
        dataIndex: 'createdDate',
        key: 'createdDate',
        render: (date: string, record) => (
          <InfoText
            text={date ? dayjs(date).format('DD MMM YYYY') : '-'}
            className="!font-normal !text-sm text-light-text-primary dark:text-dark-text-primary !leading-none"
            data-testid={`bidding-posts-date-${record.postId}`}
          />
        ),
      },
      {
        title: (
          <div
            className="flex justify-center whitespace-nowrap"
            data-testid="bidding-posts-col-actions-header"
          >
            <TitleText
              text={t('biddingPosts.actions')}
              className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary !leading-none whitespace-nowrap"
              data-testid="bidding-posts-col-actions"
            />
          </div>
        ),
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_, record) => {
          const canDelete = record.actions?.canDelete ?? false;
          const menuItems: NonNullable<MenuProps['items']> = [
            {
              key: 'title',
              label: (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="px-3 pt-1 pb-1 cursor-default select-none pointer-events-none"
                >
                  <InfoText
                    text={t('biddingPosts.postManagement')}
                    className="!text-sm text-light-text-secondary dark:text-dark-text-secondary !font-normal"
                    data-testid={`bidding-posts-menu-title-${record.postId}`}
                  />
                </div>
              ),
              disabled: true,
              className:
                '!bg-transparent hover:!bg-transparent focus:!bg-transparent active:!bg-transparent [&.ant-dropdown-menu-item]:!bg-transparent [&.ant-dropdown-menu-item:hover]:!bg-transparent [&.ant-dropdown-menu-item-active]:!bg-transparent !cursor-default !p-0 mb-1 pointer-events-none',
            },
            ...(canDelete
              ? [
                  {
                    key: 'delete',
                    label: (
                      <div
                        className="px-3 py-1.5 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletePostId(record.id ?? record.postId);
                        }}
                        data-testid={`action-delete-${record.id ?? record.postId}`}
                      >
                        <InfoText
                          text={t('biddingPosts.deletePost')}
                          className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary"
                        />
                      </div>
                    ),
                    className:
                      '!p-0 !rounded-[8px] hover:!bg-pink-100 dark:hover:!bg-pink-950/40 [&.ant-dropdown-menu-item-active]:!bg-pink-100 dark:[&.ant-dropdown-menu-item-active]:!bg-pink-950/40 transition-colors',
                  },
                ]
              : [
                  {
                    key: 'no-actions',
                    label: (
                      <div
                        className="px-3 py-1.5 cursor-default select-none pointer-events-none"
                        data-testid={`action-no-actions-${record.id ?? record.postId}`}
                      >
                        <InfoText
                          text={t('biddingPosts.noActions')}
                          className="!text-sm !font-normal text-light-text-placeholder dark:text-dark-text-placeholder"
                        />
                      </div>
                    ),
                    disabled: true,
                    className:
                      '!p-0 !bg-transparent hover:!bg-transparent cursor-default pointer-events-none',
                  },
                ]),
          ];

          return (
            <div
              className="flex justify-center items-center"
              data-testid={`bidding-posts-action-wrapper-${record.postId}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                menu={{
                  items: menuItems,
                  className:
                    '!w-[180px] !p-2 !rounded-[16px] !border !border-light-input-border dark:!border-dark-input-border !shadow-[0_8px_24px_rgba(0,0,0,0.08)] bg-white dark:!bg-dark-surface-primary [&_.ant-dropdown-menu-item-disabled]:!bg-transparent [&_.ant-dropdown-menu-item-disabled:hover]:!bg-transparent [&_.ant-dropdown-menu-item-disabled]:!cursor-default [&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover]:!bg-pink-100 dark:[&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover]:!bg-pink-950/40 [&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active]:!bg-pink-100 dark:[&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active]:!bg-pink-950/40',
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <div
                  className="w-[36px] h-[36px] p-[8px] gap-[8px] flex items-center justify-center rounded-[12px] cursor-pointer transition-colors bg-soft-background hover:bg-pink-500 [&.ant-dropdown-open]:bg-pink-500 text-black-950 hover:text-black-950 dark:text-light-button-label-primary dark:hover:text-black-950 [&.ant-dropdown-open]:text-black-950 [&.ant-dropdown-open]:dark:text-black-950"
                  data-testid={`bidding-posts-action-trigger-${record.postId}`}
                >
                  <MenuIcon size="20" className="text-gray-950" />
                </div>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [t, language]
  );

  const renderTableHeader = () => (
    <div
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 pb-0"
      data-testid="bidding-posts-card-header"
    >
      <div
        className="flex flex-col gap-1"
        data-testid="bidding-posts-title-wrapper"
      >
        <TitleText
          text={
            isDisliked
              ? t('biddingPosts.dislikedPostsCardTitle')
              : t('biddingPosts.cardTitle')
          }
          className="!text-2xl !font-bold text-light-text-primary dark:text-dark-text-primary leading-none"
          data-testid="bidding-posts-card-title"
        />
        <InfoText
          text={
            isDisliked
              ? t('biddingPosts.dislikedPostsCardDesc')
              : t('biddingPosts.cardDesc')
          }
          className="!text-sm !font-normal text-[#637381] dark:text-dark-text-secondary !leading-none"
          data-testid="bidding-posts-card-desc"
        />
      </div>

      <div
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto"
        data-testid="bidding-posts-card-actions"
      >
        <Search
          searchName="biddingPostsSearch"
          inputWidth="!w-[330px] !h-[46px] !p-3 !gap-2 !rounded !border !border-light-input-border dark:!border-dark-input-border opacity-100"
          data-testid="bidding-posts-search-input"
        />
        <Button
          size="md"
          className={`!rounded !h-[46px] !border-light-input-border dark:!border-dark-input-border !px-4 !py-3 !gap-2.5 whitespace-nowrap w-full sm:w-auto justify-center cursor-pointer !bg-white dark:!bg-dark-surface-primary !text-light-text-primary dark:!text-dark-text-primary`}
          variant="outline"
          onClick={() => setIsFilterOpen(true)}
          icon={
            <Filter
              size={18}
              variant={'Outline'}
              className="text-light-text-primary dark:text-dark-text-primary"
            />
          }
          data-testid="bidding-posts-filter-btn"
        >
          {t('biddingPosts.filterBtn')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="bidding-posts-table-container">
      {renderTableHeader()}
      <div data-testid="bidding-posts-table-wrapper">
        <CustomTable
          columns={columns}
          dataSource={tableData}
          rowKey={(record) => record.id ?? record.postId ?? record.serial}
          loading={isFetching}
          rowClassName={(_, index) =>
            index % 2 !== 0
              ? 'cursor-pointer [&>td]:!bg-soft-background dark:[&>td]:!bg-gray-800/50 [&>td:first-child]:rounded-l-xl [&>td:last-child]:rounded-r-xl [&>td]:transition-colors'
              : 'cursor-pointer [&>td]:!bg-white dark:[&>td]:!bg-gray-900 [&>td]:transition-colors'
          }
          onRowClick={(_, record: BiddingPostItem) => {
            const targetId = record.id ?? record.postId;
            const basePath = isDisliked ? '/disliked-jobs' : '/bidding-posts';
            navigate(`${basePath}/${targetId}`);
          }}
          currentPage={page}
          onPageChange={(p) => dispatch(setPage(p - 1))}
          totalElements={totalElements}
          scroll={tableData.length > 0 ? { x: 'max-content' } : undefined}
          data-testid="bidding-posts-custom-table"
        />
      </div>
      <DeleteModal
        isOpen={Boolean(deletePostId)}
        onClose={() => setDeletePostId(null)}
        onDelete={handleConfirmDelete}
        isLoading={isDeleting}
      />
      <BiddingPostsFilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </div>
  );
}
